// Shared filter bar for every report page.
//
// Composes the Orio360 <TimeRangeFilter>, a compact location <Select>, and an
// `extraFilters` slot for report-specific controls (e.g. Top-N, granularity).
// The bar collapses to a vertical stack on mobile per RESPONSIVE_DESIGN.md.

import type { ReactNode } from 'react';
import { TimeRangeFilter, Select } from '@bbpos/ui';
import type { TimeRangeValue, TimeRangePreset } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';

const REPORT_PRESETS: TimeRangePreset[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 Days' },
  { id: 'last30', label: 'Last 30 Days' },
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: 'thisQuarter', label: 'This Quarter (FY)' },
  { id: 'lastQuarter', label: 'Last Quarter (FY)' },
  { id: 'thisFinancialYear', label: 'This Financial Year' },
  { id: 'lastFinancialYear', label: 'Last Financial Year' },
  { id: 'custom', label: 'Custom' },
];

export interface ReportFilterBarProps {
  timeRange: TimeRangeValue;
  onTimeRangeChange: (value: TimeRangeValue) => void;
  locationId?: string;
  onLocationChange?: (id: string | undefined) => void;
  showLocation?: boolean;
  /** false for an "as of today" report (payables ageing) — the bar keeps only the store picker. */
  showTimeRange?: boolean;
  extraFilters?: ReactNode;
}

export function ReportFilterBar({
  timeRange,
  onTimeRangeChange,
  locationId,
  onLocationChange,
  showLocation = true,
  showTimeRange = true,
  extraFilters,
}: ReportFilterBarProps) {
  const profile = useAuthStore((s) => s.profile);
  const locations = (profile?.locations ?? []).filter((l) => l.is_active);

  const locationOptions = [
    { value: '', label: 'All Locations' },
    ...locations.map((l) => ({ value: l.id, label: l.name })),
  ];

  return (
    <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-4 sm:p-5 ring-1 ring-neutral-100/80 dark:ring-neutral-700/60">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        {showTimeRange ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 min-w-0">
            <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Period
            </span>
            <div className="min-w-0">
              <TimeRangeFilter value={timeRange} onChange={onTimeRangeChange} presets={REPORT_PRESETS} />
            </div>
          </div>
        ) : (
          <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            As of today
          </span>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {showLocation && locations.length > 0 && onLocationChange ? (
            <div className="w-full sm:w-52">
              <Select
                options={locationOptions}
                value={locationId ?? ''}
                onChange={(v) => onLocationChange(typeof v === 'string' && v ? v : undefined)}
                placeholder="All Locations"
              />
            </div>
          ) : null}
          {extraFilters}
        </div>
      </div>
    </div>
  );
}
